import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLazyGetDataQuery } from "../../app/services/commonApi";
import { pick } from "./copilotUtils";

const AT_ENTITY_KINDS = [
  { kind: "job", word: "job", title: "Published openings", insert: "@#J " },
  { kind: "status", word: "status", title: "Applicant statuses", insert: "@#S " },
  { kind: "candidate", word: "candidate", title: "Candidates", insert: "@candidate " },
  { kind: "batch", word: "batch", title: "Resume batches", insert: "@batch " },
];

/**
 * Detect /export or @entity typing triggers at the caret.
 * Supports partial tokens: @, @s, @st → status; @j → job; etc.
 */
export const detectTrigger = (value, caret) => {
  const text = String(value || "");
  const pos = typeof caret === "number" ? caret : text.length;
  const before = text.slice(0, pos);
  const at = before.lastIndexOf("@");
  const slash = before.lastIndexOf("/export");
  // Prefer the later trigger so `Export applicants @s` is not stolen by `/export`.
  const start = at >= slash ? at : slash;
  if (start < 0) return null;
  const token = before.slice(start);

  if (token.startsWith("/export") && (at < 0 || slash > at)) {
    const q = token.slice("/export".length).trim();
    return { kind: "export", query: q, start, end: pos };
  }

  // Shorthand entity pickers:
  // - `@#J` => published jobs (same as `@job`)
  // - `@#S` => applicant statuses (same as `@status`)
  if (/^@#([jJsS])/.test(token)) {
    const letter = token.slice(2, 3).toUpperCase();
    const kind = letter === "J" ? "job" : "status";
    return { kind, query: "", start, end: pos };
  }

  if (!token.startsWith("@")) return null;

  const full = /^@(job|status|candidate|batch)(?:\s+(.*))?$/i.exec(token);
  if (full) {
    const kind = full[1].toLowerCase();
    const query = (full[2] || "").trimStart();
    return {
      kind,
      query,
      start,
      end: pos,
      waiting: kind === "candidate" && query.length < 2,
    };
  }

  // Partial entity prefix: @ / @s / @jo / @cand … (ignore trailing punctuation like @/)
  const partial = /^@([a-z]*)[^a-z]*$/i.exec(token);
  if (!partial) return null;
  const typed = partial[1].toLowerCase();
  const matches = AT_ENTITY_KINDS.filter((k) => k.word.startsWith(typed));
  if (matches.length === 1) {
    return {
      kind: matches[0].kind,
      query: "",
      start,
      end: pos,
      waiting: matches[0].kind === "candidate",
    };
  }
  if (matches.length > 1) {
    return {
      kind: "at-menu",
      query: typed,
      start,
      end: pos,
      options: matches,
    };
  }
  return null;
};

export const useCopilotComposerSnippets = (input, setInput) => {
  const [fetchData] = useLazyGetDataQuery();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [active, setActive] = useState(null);
  const debounceRef = useRef(null);

  const load = useCallback(
    async (trigger) => {
      if (!trigger) return;

      if (trigger.kind === "at-menu") {
        setItems(
          (trigger.options || []).map((o) => ({
            kind: "at-menu",
            token: o.insert,
            label: `${o.insert.trim()} — ${o.title}`,
          }))
        );
        setTitle("Insert entity");
        setOpen(true);
        return;
      }

      if (trigger.kind === "export") {
        try {
          const res = await fetchData({ url: "/AIExecution/CopilotExports" }).unwrap();
          const rows = pick(res, "data", "Data") || res || [];
          const q = (trigger.query || "").toLowerCase();
          const list = (Array.isArray(rows) ? rows : []).filter((d) => {
            if (!q) return true;
            const label = String(pick(d, "label", "Label") || "").toLowerCase();
            const hint = String(pick(d, "snippetHint", "SnippetHint") || "").toLowerCase();
            return label.includes(q) || hint.includes(q);
          });
          setItems(
            list.map((d) => ({
              kind: "export",
              token: pick(d, "snippetHint", "SnippetHint") || pick(d, "label", "Label"),
              label: pick(d, "label", "Label"),
            }))
          );
          setTitle("Export kinds");
          setOpen(list.length > 0);
        } catch {
          setOpen(false);
        }
        return;
      }

      if (trigger.kind === "candidate" && trigger.waiting) {
        setItems([]);
        setTitle("Type 2+ letters to search candidates");
        setOpen(true);
        return;
      }

      try {
        const res = await fetchData({
          url: "/AIExecution/CopilotSnippets",
          data: { kind: trigger.kind, q: trigger.query || "" },
        }).unwrap();
        const rows = pick(res, "data", "Data") || [];
        setItems(
          (Array.isArray(rows) ? rows : []).map((r) => ({
            kind: pick(r, "kind", "Kind"),
            token: pick(r, "token", "Token"),
            label: pick(r, "label", "Label"),
          }))
        );
        setTitle(
          {
            job: "Published openings",
            status: "Applicant statuses",
            candidate: "Candidates",
            batch: "Resume batches",
          }[trigger.kind] || "Suggestions"
        );
        setOpen(true);
      } catch {
        setOpen(false);
      }
    },
    [fetchData]
  );

  useEffect(() => {
    const el = document.querySelector(".copilot-composer__input");
    const caret = el?.selectionStart ?? input.length;
    const trigger = detectTrigger(input, caret);
    setActive(trigger);
    if (!trigger) {
      setOpen(false);
      return undefined;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(trigger), trigger.kind === "candidate" ? 280 : 120);
    return () => clearTimeout(debounceRef.current);
  }, [input, load]);

  const pickItem = useCallback(
    (item) => {
      if (!active || !item?.token) return;
      const before = input.slice(0, active.start);
      const after = input.slice(active.end);
      const next = `${before}${item.token} ${after}`.replace(/\s+/g, " ").trimStart();
      setInput(next);
      setOpen(false);
      setActive(null);
      requestAnimationFrame(() => document.querySelector(".copilot-composer__input")?.focus?.());
    },
    [active, input, setInput]
  );

  const snippetPanel = useMemo(
    () =>
      open ? (
        <div className="copilot-snippet-panel">
          <div className="copilot-snippet-panel__title">{title}</div>
          {items.length === 0 ? (
            <div className="copilot-snippet-panel__empty">No matches</div>
          ) : (
            items.map((item) => (
              <button
                key={`${item.kind}-${item.token}`}
                type="button"
                className="copilot-snippet-panel__item"
                onMouseDown={(e) => {
                  e.preventDefault();
                  pickItem(item);
                }}
              >
                {item.label || item.token}
              </button>
            ))
          )}
        </div>
      ) : null,
    [items, open, pickItem, title]
  );

  return { snippetPanel, snippetOpen: open };
};

export default useCopilotComposerSnippets;
